import { useQuery } from '@tanstack/react-query'
import { useAuthenticatedClient } from './queries'
import type { components } from './schema'

// Selling from our own warehouse on dropship and quote lines, and its POs (USA-1574).
export type WarehouseSource = components['schemas']['WarehouseSourceRead']
export type WarehouseSourceChoice = components['schemas']['WarehouseSourceChoice']
export type WarehouseStockOption = components['schemas']['WarehouseStockOptionRead']
export type WarehousePurchaseOrder = components['schemas']['WarehousePullRead']

export const warehouseSourcingKeys = {
  all: ['warehouse-sourcing'] as const,
  stock: (productIds: readonly number[]) => ['warehouse-sourcing', 'stock', productIds] as const,
  purchaseOrder: (poUuid: string) => ['warehouse-sourcing', 'purchase-order', poUuid] as const,
}

/** What we hold of these products, per condition; products we hold none of are absent. */
export function useWarehouseStock(productIds: readonly number[]) {
  const getClient = useAuthenticatedClient()
  const ids = [...new Set(productIds)].sort((a, b) => a - b)
  return useQuery({
    queryKey: warehouseSourcingKeys.stock(ids),
    enabled: ids.length > 0,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warehouse-sourcing/stock', {
        params: { query: { product_ids: ids } },
      })
      if (error) throw error
      return new Map(data.map((product) => [product.product_id, product.options]))
    },
    staleTime: 30_000,
  })
}

/** Whether a PO is pulled from our own warehouse, and the bay and pallet of what it takes. */
export function useWarehousePurchaseOrder(poUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: warehouseSourcingKeys.purchaseOrder(poUuid),
    enabled: Boolean(poUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warehouse-sourcing/purchase-orders/{po_uuid}', {
        params: { path: { po_uuid: poUuid } },
      })
      if (error) throw error
      return data
    },
  })
}
